import { useState, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { DR_PHOTO, SDL_LOGO, LDA_LOGO } from "@/lib/assets";

export default function HeroSection({ onBook }) {
  const [hovered, setHovered] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 150);
    return () => clearTimeout(t);
  }, []);

  const patientFlex = hovered === "patient" ? 5 : hovered === "academy" ? 2.5 : 3.5;
  const bridgeFlex = hovered ? 2.5 : 3.5;
  const academyFlex = hovered === "academy" ? 5 : hovered === "patient" ? 2.5 : 3.5;
  const patientShadowOpacity = hovered === "patient" ? 1 : 0;
  const academyShadowOpacity = hovered === "academy" ? 1 : 0;
  const edgePanelPadding = "96px clamp(28px, 3.2vw, 48px) 44px";
  const edgeContentWidth = "clamp(18rem, 70%, 22rem)";
  const edgeMetaWidth = "clamp(16rem, 66%, 22rem)";

  return (
    <section data-testid="hero-section" className="relative w-full" style={{ height: "100vh", minHeight: "620px" }}>
      <div
        className="absolute top-0 left-0 right-0 pointer-events-none"
        style={{
          zIndex: 44,
          height: "200px",
          background: "radial-gradient(120% 78% at 50% 0%, rgba(0,0,0,0.48) 0%, rgba(0,0,0,0.16) 42%, transparent 74%), linear-gradient(to bottom, rgba(0,0,0,0.44) 0%, rgba(0,0,0,0.12) 52%, transparent 100%)",
        }}
      />
      <div
        className="absolute top-0 left-0 right-0 pointer-events-none hidden md:block"
        style={{
          zIndex: 45,
          height: "280px",
          opacity: patientShadowOpacity,
          background:
            "radial-gradient(120% 82% at 14% 0%, rgba(0,0,0,0.92) 0%, rgba(22,17,15,0.62) 28%, rgba(22,17,15,0.24) 54%, transparent 76%), linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.38) 30%, rgba(22,17,15,0.14) 58%, transparent 100%)",
          transition: "opacity 0.42s cubic-bezier(0.22, 1, 0.36, 1)",
          willChange: "opacity",
        }}
      />
      <div
        className="absolute top-0 left-0 right-0 pointer-events-none hidden md:block"
        style={{
          zIndex: 45,
          height: "280px",
          opacity: academyShadowOpacity,
          background:
            "radial-gradient(120% 82% at 86% 0%, rgba(0,0,0,0.92) 0%, rgba(8,32,37,0.64) 28%, rgba(8,32,37,0.24) 54%, transparent 76%), linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.38) 30%, rgba(8,32,37,0.14) 58%, transparent 100%)",
          transition: "opacity 0.42s cubic-bezier(0.22, 1, 0.36, 1)",
          willChange: "opacity",
        }}
      />

      <div className="hidden md:flex w-full h-full overflow-hidden" aria-hidden="false">
        <div
          data-testid="hero-patient-panel"
          className="hero-panel relative cursor-pointer"
          style={{
            flex: patientFlex,
            background: "#16110F",
            borderRight: "1px solid #3A2E28",
            padding: edgePanelPadding,
            minWidth: 0,
          }}
          onMouseEnter={() => setHovered("patient")}
          onMouseLeave={() => setHovered(null)}
        >
          <div
            className="absolute top-0 left-0 w-full h-[2px]"
            style={{
              background: "linear-gradient(90deg, #E3C15A 0%, #D4AF37 42%, rgba(212,175,55,0.12) 100%)",
              opacity: hovered === "patient" ? 1 : 0.34,
              transform: hovered === "patient" ? "scaleX(1)" : "scaleX(0.82)",
              transformOrigin: "left center",
              boxShadow: hovered === "patient" ? "0 0 18px rgba(212,175,55,0.38)" : "0 0 0 rgba(212,175,55,0)",
              transition: "opacity 0.42s cubic-bezier(0.22, 1, 0.36, 1), transform 0.48s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.42s ease",
              zIndex: 46,
            }}
          />

          <div className="flex h-full flex-col justify-between">
            <div
              className="ml-auto flex flex-col items-end text-right"
              style={{ width: edgeContentWidth, marginTop: "clamp(0.75rem, 4vh, 3.75rem)" }}
            >
              <div className="mb-7 h-10 overflow-hidden" style={{ maxWidth: "168px", background: "#16110F" }}>
                <img
                  src={SDL_LOGO}
                  alt="Specialist Dental London"
                  className="h-full w-auto object-contain"
                  style={{ filter: "brightness(0) invert(1)", opacity: 0.88 }}
                />
              </div>

              <p className="font-outfit text-xs tracking-[0.25em] uppercase font-semibold mb-4" style={{ color: "#D4AF37" }}>
                Specialist Dental London
              </p>

              <h2
                className="font-cormorant font-light leading-none mb-6"
                style={{
                  fontSize: hovered === "patient" ? "clamp(2.7rem, 4.2vw, 4rem)" : "clamp(2.05rem, 3vw, 3rem)",
                  color: "#FDF8ED",
                  transition: "font-size 0.5s ease",
                  opacity: loaded ? 1 : 0,
                  transform: loaded ? "translateY(0)" : "translateY(20px)",
                  transitionProperty: "font-size, opacity, transform",
                  transitionDuration: "0.5s",
                }}
              >
                Expert Care.
                <br />
                <em style={{ color: "#D4AF37" }}>Precisely</em> Delivered.
              </h2>

              <p
                className="font-outfit leading-relaxed mb-8"
                style={{
                  color: "#A89886",
                  fontSize: "clamp(0.8rem, 1vw, 0.94rem)",
                  maxWidth: "21rem",
                  opacity: hovered === "patient" ? 1 : 0.78,
                  transition: "opacity 0.3s",
                }}
              >
                Specialist-led implants, restorative, and surgical dental care in London. Led by a consultant with 25+ years of expertise.
              </p>

              <button
                data-testid="hero-patient-book-btn"
                onClick={() => onBook("patient")}
                className="flex flex-row-reverse items-center gap-3 font-outfit text-sm font-medium px-6 py-3 transition-all duration-200 hover:gap-4 group"
                style={{ background: "#D4AF37", color: "#16110F" }}
              >
                Book a Consultation
                <ArrowRight size={15} className="rotate-180 transition-transform duration-200 group-hover:-translate-x-1" />
              </button>
            </div>

            <div className="ml-auto flex flex-wrap justify-end gap-2" style={{ width: edgeMetaWidth }}>
              {["Implants", "Perio", "Endo", "Prostho"].map((s) => (
                <span key={s} className="font-outfit text-xs px-2 py-0.5 border" style={{ borderColor: "#3A2E28", color: "#78716C" }}>
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div
          data-testid="hero-bridge"
          className="hero-panel relative flex flex-col items-center justify-end overflow-hidden"
          style={{ flex: bridgeFlex, background: "#0D0B0A", minWidth: 0 }}
        >
          <img
            src={DR_PHOTO}
            alt="Dr. Shihab Romeed"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "50% 15%", filter: "brightness(0.9) contrast(1.04)" }}
          />
          <div
            className="absolute inset-x-0 bottom-0 z-10"
            style={{ height: "42%", background: "linear-gradient(to top, #0D0B0A 15%, transparent)" }}
          />
          <div className="relative z-20 text-center pb-8 px-4">
            <div className="mx-auto mb-3" style={{ width: "48px", height: "1px", background: "linear-gradient(90deg, #D4AF37, #1CB0C6)" }} />
            <h3 className="font-cormorant text-xl font-light tracking-wide" style={{ color: "#FDF8ED" }}>
              Dr. Shihab Romeed
            </h3>
            <p className="font-outfit text-xs tracking-[0.14em] uppercase mt-1" style={{ color: "#A89886" }}>
              Consultant · Restorative Dentistry
            </p>
          </div>
          <div className="absolute left-0 top-0 bottom-0 w-px" style={{ background: "linear-gradient(to bottom, transparent, rgba(212,175,55,0.5) 50%, transparent)" }} />
          <div className="absolute right-0 top-0 bottom-0 w-px" style={{ background: "linear-gradient(to bottom, transparent, rgba(28,176,198,0.5) 50%, transparent)" }} />
        </div>

        <div
          data-testid="hero-academy-panel"
          className="hero-panel relative cursor-pointer"
          style={{
            flex: academyFlex,
            background: "#082025",
            borderLeft: "1px solid #164B55",
            padding: edgePanelPadding,
            minWidth: 0,
          }}
          onMouseEnter={() => setHovered("academy")}
          onMouseLeave={() => setHovered(null)}
        >
          <div
            className="absolute top-0 left-0 w-full h-[2px]"
            style={{
              background: "linear-gradient(90deg, rgba(28,176,198,0.12) 0%, #1CB0C6 58%, #6AD8E7 100%)",
              opacity: hovered === "academy" ? 1 : 0.34,
              transform: hovered === "academy" ? "scaleX(1)" : "scaleX(0.82)",
              transformOrigin: "right center",
              boxShadow: hovered === "academy" ? "0 0 18px rgba(28,176,198,0.34)" : "0 0 0 rgba(28,176,198,0)",
              transition: "opacity 0.42s cubic-bezier(0.22, 1, 0.36, 1), transform 0.48s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.42s ease",
              zIndex: 46,
            }}
          />

          <div className="flex h-full flex-col justify-between">
            <div
              className="flex flex-col items-start text-left"
              style={{ width: edgeContentWidth, marginTop: "clamp(0.75rem, 4vh, 3.75rem)" }}
            >
              <div className="mb-7 h-10 overflow-hidden" style={{ maxWidth: "168px", background: "#082025" }}>
                <img
                  src={LDA_LOGO}
                  alt="London Dental Academy"
                  className="h-full w-auto object-contain"
                  style={{ filter: "brightness(0) saturate(100%) invert(60%) sepia(100%) saturate(400%) hue-rotate(160deg) brightness(0.95)" }}
                />
              </div>

              <p className="font-outfit text-xs tracking-[0.25em] uppercase font-semibold mb-4" style={{ color: "#1CB0C6" }}>
                London Dental Academy
              </p>

              <h2
                className="font-cormorant font-light leading-none mb-6"
                style={{
                  fontSize: hovered === "academy" ? "clamp(2.7rem, 4.2vw, 4rem)" : "clamp(2.05rem, 3vw, 3rem)",
                  color: "#F0FBFC",
                  transition: "font-size 0.5s ease",
                  opacity: loaded ? 1 : 0,
                  transform: loaded ? "translateY(0)" : "translateY(20px)",
                  transitionProperty: "font-size, opacity, transform",
                  transitionDuration: "0.5s, 0.7s, 0.7s",
                }}
              >
                Clinical Mastery.
                <br />
                <em style={{ color: "#1CB0C6" }}>Taught</em> by Specialists.
              </h2>

              <p
                className="font-outfit leading-relaxed mb-8"
                style={{
                  color: "#83A9AF",
                  fontSize: "clamp(0.8rem, 1vw, 0.94rem)",
                  maxWidth: "21rem",
                  opacity: hovered === "academy" ? 1 : 0.78,
                  transition: "opacity 0.3s",
                }}
              >
                Hands-on advanced training for dental professionals - taught by a Royal College Fellow with a PhD.
              </p>

              <button
                data-testid="hero-academy-course-btn"
                onClick={() => onBook("professional")}
                className="flex items-center gap-3 font-outfit text-sm font-medium px-6 py-3 transition-all duration-200 hover:gap-4 group"
                style={{ background: "#1CB0C6", color: "#082025" }}
              >
                Explore Courses
                <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
              </button>
            </div>

            <div className="flex flex-wrap gap-2" style={{ width: edgeMetaWidth }}>
              {["Implantology", "Endo", "Sinus Lift", "CPD"].map((c) => (
                <span key={c} className="font-outfit text-xs px-2 py-0.5 border" style={{ borderColor: "#164B55", color: "#4B7D87" }}>
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="md:hidden flex flex-col h-full" style={{ background: "#0D0B0A" }}>
        <div className="relative flex-1 overflow-hidden">
          <img src={DR_PHOTO} alt="Dr. Shihab Romeed" className="w-full h-full object-cover" style={{ objectPosition: "50% 15%", filter: "brightness(0.82)" }} />
          <div className="absolute inset-x-0 bottom-0" style={{ height: "50%", background: "linear-gradient(to top, #0D0B0A, transparent)" }} />
          <div className="absolute bottom-6 left-0 right-0 text-center px-4 z-10">
            <p className="font-outfit text-xs tracking-[0.2em] uppercase mb-2" style={{ color: "#D4AF37" }}>
              Consultant · Clinician · Educator
            </p>
            <h1 className="font-cormorant text-4xl font-light" style={{ color: "#FDF8ED" }}>
              Dr. Shihab Romeed
            </h1>
            <p className="font-outfit text-xs mt-1" style={{ color: "#A89886" }}>
              Consultant in Restorative Dentistry
            </p>
          </div>
        </div>
        <div className="flex" style={{ minHeight: "200px" }}>
          <button
            data-testid="hero-mobile-patient-btn"
            onClick={() => onBook("patient")}
            className="flex-1 flex flex-col items-start justify-center gap-2 p-6 border-r active:opacity-80"
            style={{ background: "#16110F", borderColor: "#3A2E28" }}
          >
            <span className="font-outfit text-xs tracking-widest uppercase" style={{ color: "#D4AF37" }}>
              For Patients
            </span>
            <h3 className="font-cormorant text-xl font-light" style={{ color: "#FDF8ED" }}>
              Specialist Care
            </h3>
            <span className="font-outfit text-xs flex items-center gap-1 mt-1" style={{ color: "#D4AF37" }}>
              Book Now <ArrowRight size={12} />
            </span>
          </button>
          <button
            data-testid="hero-mobile-academy-btn"
            onClick={() => onBook("professional")}
            className="flex-1 flex flex-col items-start justify-center gap-2 p-6 active:opacity-80"
            style={{ background: "#082025" }}
          >
            <span className="font-outfit text-xs tracking-widest uppercase" style={{ color: "#1CB0C6" }}>
              For Clinicians
            </span>
            <h3 className="font-cormorant text-xl font-light" style={{ color: "#F0FBFC" }}>
              Academy Courses
            </h3>
            <span className="font-outfit text-xs flex items-center gap-1 mt-1" style={{ color: "#1CB0C6" }}>
              Explore <ArrowRight size={12} />
            </span>
          </button>
        </div>
      </div>
    </section>
  );
}
